import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
// adjust path to your slice
// adjust path

import "./MoveProductFromStoreOverlay.css";
import { uiAlert } from "../../../../components/alerts/customAlert/uiAlert";
import { setWarehouse } from "../../../../slice/warehouseSlice";
import { findMatchingProducts } from "../../warehouse/newInterface/WarehouseHouseN";
import { ButtonLoaderII } from "../../../../components/uxCollections/Loaders";
import { createProductUrl, getWarehouseUrl, moveToStoreUrl, moveStoreToWarehouseUrl, moveStoreToStoreUrl } from "../../../../api/endpoints";

const STEPS = { FORM: "form", REVIEW: "review", SUCCESS: "success" };

// ── adjust these to match your actual API base urls ──

/**
 * MoveProductFromStoreOverlay
 *
 * Moves a product between locations:
 *  - warehouse -> store   (original flow)
 *  - store     -> store
 *  - store     -> warehouse   (any of the user's warehouses)
 *
 * Props:
 *  - product          {object} the product being moved
 *  - onClose          {function}
 *  - sourceType        "warehouse" | "store"
 *  - sourceID         {string} the current warehouseId or storeId the product is moving FROM
 *  - currency         {string}
 *  - onMove           {function} optional callback after a successful move
 */

export function MoveProductFromStoreOverlay({ product, onClose, sourceType, sourceID, currency, onMove, updateSelectedProduct }) {
  const [step, setStep] = useState(STEPS.FORM);

  // destinationValue is "warehouse:<id>" or "store:<id>"
  const [destinationValue, setDestinationValue] = useState("");
  const [quantity, setQuantity] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [choice, setChoice] = useState(null); // "restock" | "create"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { user } = useSelector((state) => state.webStoreUser);
  const stores = user?.store || [];
  const warehouses = user?.warehouse || [];
  const dispatch = useDispatch();

  // stores the product can move to — exclude the store it's currently in
  const destinationStores = stores.filter((s) => sourceType !== "store" || s._id !== sourceID);
  // warehouses the product can move to — only offered when moving FROM a store;
  // exclude the warehouse it's currently in if moving between warehouses in future
  const destinationWarehouses = sourceType === "store" ? warehouses.filter((w) => w.warehouseId !== sourceID) : [];

  const [destinationType, destinationID] = destinationValue ? destinationValue.split(":") : ["", ""];
  const destinationStore = destinationType === "store" ? destinationStores.find((s) => s._id === destinationID) : null;
  const destinationWarehouse = destinationType === "warehouse" ? destinationWarehouses.find((w) => w.warehouseId === destinationID) : null;
  const destinationLabel = destinationType === "warehouse" ? destinationWarehouse?.warehouseName : destinationStore?.storeName;

  const availableQty = product.quantity ?? 0;

  const getStoreProducts = async (storeId) => {
    const accessToken = localStorage.getItem("accessToken");
    try {
      const response = await fetch(createProductUrl + storeId, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 404) {
        uiAlert("Store not found", "error");
        return [];
      }
      if (response.ok) {
        const collection = await response.json();
        return collection.products;
      }
    } catch (err) {
      console.log(err);
      uiAlert("An error occurred, please try again later", "error");
    }
    return [];
  };

  const getWarehouseProducts = async (whID) => {
    const accessToken = localStorage.getItem("accessToken");
    try {
      const response = await fetch(getWarehouseUrl + whID, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 404) {
        uiAlert("Warehouse not found", "error");
        return [];
      }
      if (response.ok) {
        const data = await response.json();
        return data.productsInWarehouse || data.products || [];
      }
    } catch (err) {
      console.log(err);
      uiAlert("An error occurred, please try again later", "error");
    }
    return [];
  };

  async function handleCheckDestination() {
    setError("");
    const qty = parseInt(quantity, 10);
    if (!destinationValue) return setError("Please select a destination.");
    if (!qty || qty <= 0) return setError("Enter a valid quantity.");
    if (qty > availableQty) return setError(`Only ${availableQty} units available.`);

    setLoading(true);
    try {
      const destinationProducts = destinationType === "warehouse" ? await getWarehouseProducts(destinationID) : await getStoreProducts(destinationID);

      const found = findMatchingProducts(destinationProducts || [], product.name, product.brand);
      setMatches(found);
      setChoice(found.length > 0 ? null : "create");
      setSelectedMatchId(found.length === 1 ? found[0]._id : null);
      setStep(STEPS.REVIEW);
    } catch (err) {
      setError("Failed to fetch destination products. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMove() {
    try {
      setLoading(true);
      const qty = parseInt(quantity, 10);
      const target = choice === "restock" ? matches.find((m) => m._id === selectedMatchId) : null;
      const accessToken = localStorage.getItem("accessToken");

      const basePayload = {
        product,
        quantity: qty,
        employee: null,
        productToRestockID: target?._id ?? null,
      };

      let url;
      let payload;

      if (sourceType === "warehouse") {
        // existing, unchanged flow
        url = `${moveToStoreUrl}${sourceID}/?moveIntention=${choice}`;
        payload = {
          ...basePayload,
          storeMovedToID: destinationID,
          storeMovedToName: destinationStore?.storeName,
        };
      } else if (destinationType === "warehouse") {
        url = `${moveStoreToWarehouseUrl}${sourceID}/?moveIntention=${choice}`;
        payload = {
          ...basePayload,
          warehouseMovedToID: destinationID,
        };
      } else {
        url = `${moveStoreToStoreUrl}${sourceID}/?moveIntention=${choice}`;
        payload = {
          ...basePayload,
          storeMovedToID: destinationID,
          storeMovedToName: destinationStore?.storeName,
        };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.status === 404 || response.status === 403) {
        return uiAlert(data.message, "error");
      }
      if (response.ok) {
        if (sourceType === "warehouse") {
          dispatch(setWarehouse(data));
        } else if (data.sourceProduct) {
          updateSelectedProduct?.(data.sourceProduct);
        }
        onMove?.(data);
        uiAlert("Product moved successfully", "success");
        setSelectedMatchId(null);
        setStep(STEPS.SUCCESS);
        return;
      }
      throw new Error("Failed to move product");
    } catch (err) {
      console.log(err);
      uiAlert("An error occurred while moving the product", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className='mpo-backdrop' onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className='mpo-card'>
        {/* ── STEP 1: FORM ── */}
        {step === STEPS.FORM && (
          <>
            <div className='mpo-header'>
              <h2 className='mpo-header-title'>Move product</h2>
              <button className='mpo-close-btn' onClick={onClose}>
                ×
              </button>
            </div>

            <div className='mpo-product-chip'>
              <div className='mpo-product-chip-img'>
                {product.productImage?.[0] ? <img src={product.productImage[0]} alt={product.name} /> : <span className='mpo-product-chip-placeholder' />}
              </div>
              <div className='mpo-product-chip-info'>
                <p className='mpo-product-chip-name'>{product.name}</p>
                <p className='mpo-product-chip-sub'>
                  {currency}
                  {product.costPrice} cost · {product.brand} · {product.productCategory}
                </p>
              </div>
              <div className='mpo-product-chip-qty'>
                <span className='mpo-product-chip-qty-num'>{availableQty}</span>
                <span className='mpo-product-chip-qty-label'>Available</span>
              </div>
            </div>

            <div className='mpo-field'>
              <label className='mpo-label'>Destination</label>
              <select className='mpo-select' value={destinationValue} onChange={(e) => setDestinationValue(e.target.value)}>
                <option value=''>Select destination</option>
                {destinationWarehouses.length > 0 && (
                  <optgroup label='Warehouses'>
                    {destinationWarehouses.map((w) => (
                      <option key={w.warehouseId} value={`warehouse:${w.warehouseId}`}>
                        {w.warehouseName}
                      </option>
                    ))}
                  </optgroup>
                )}
                {destinationStores.length > 0 && (
                  <optgroup label='Stores'>
                    {destinationStores.map((s) => (
                      <option key={s._id} value={`store:${s._id}`}>
                        {s.storeName}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className='mpo-field'>
              <label className='mpo-label'>Quantity to move</label>
              <input
                className='mpo-input'
                type='number'
                min={1}
                max={availableQty}
                placeholder='Enter quantity to move'
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>

            <p className='mpo-error-text'>{error}</p>

            <button className='mpo-btn-primary' onClick={handleCheckDestination} disabled={loading}>
              {loading ? "Checking..." : "Move"}
            </button>
          </>
        )}

        {/* ── STEP 2: REVIEW ── */}
        {step === STEPS.REVIEW && (
          <>
            <div className='mpo-back-header'>
              <button className='mpo-back-btn' onClick={() => setStep(STEPS.FORM)}>
                <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='M15 18l-6-6 6-6' />
                </svg>
              </button>
              <h2 className='mpo-header-title'>Review move</h2>
            </div>

            <div className='mpo-summary-bar'>
              <span>
                Moving <b>{quantity} units</b> of <b>{product.name}</b>
              </span>
              <span className='mpo-summary-dest'>→ {destinationLabel}</span>
            </div>

            {matches.length > 0 ? (
              <>
                <div className='mpo-warn-banner'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#b45309' strokeWidth='2' style={{ flexShrink: 0, marginTop: 2 }}>
                    <path d='M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' />
                    <line x1='12' y1='9' x2='12' y2='13' />
                    <line x1='12' y1='17' x2='12.01' y2='17' />
                  </svg>
                  <p className='mpo-warn-banner-text'>
                    {matches.length === 1
                      ? `A duplicate was found in ${destinationLabel} with the same or similar name and brand.`
                      : `${matches.length} duplicates found in ${destinationLabel} with the same name and brand.`}{" "}
                    Select one to restock or create a new product entry.
                  </p>
                </div>

                <p className='mpo-match-label'>Existing matches</p>

                {matches.map((m) => {
                  const active = choice === "restock" && selectedMatchId === m._id;
                  return (
                    <div
                      key={m._id}
                      className={`mpo-radio-row${active ? " mpo-radio-row--active" : ""}`}
                      onClick={() => {
                        setChoice("restock");
                        setSelectedMatchId(m._id);
                      }}
                    >
                      <div className='mpo-radio-row-info'>
                        <p className='mpo-radio-row-name'>{m.name}</p>
                        <p className='mpo-radio-row-sub'>
                          {(m.quantity ?? 0).toLocaleString("en-NG")} in stock
                          {m.costPrice ? ` · ${currency}${m.costPrice.toLocaleString("en-NG")}` : ""}
                          {m.brand && m.brand !== "Unknown Brand" ? ` · ${m.brand}` : ""}
                        </p>
                      </div>
                      <div className='mpo-radio-ring'>{active && <div className='mpo-radio-dot' />}</div>
                    </div>
                  );
                })}

                <div
                  className={`mpo-radio-row${choice === "create" ? " mpo-radio-row--active" : ""}`}
                  onClick={() => {
                    setChoice("create");
                    setSelectedMatchId(null);
                  }}
                >
                  <div className='mpo-radio-row-info'>
                    <p className='mpo-radio-row-name'>Create as new product</p>
                    <p className='mpo-radio-row-sub'>Add as a separate product entry even though a match exists</p>
                  </div>
                  <div className='mpo-radio-ring'>{choice === "create" && <div className='mpo-radio-dot' />}</div>
                </div>
              </>
            ) : (
              <div className='mpo-nodupe-banner'>
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#166534' strokeWidth='2' style={{ flexShrink: 0, marginTop: 2 }}>
                  <path d='M22 11.08V12a10 10 0 11-5.93-9.14' />
                  <polyline points='22 4 12 14.01 9 11.01' />
                </svg>
                <p className='mpo-nodupe-banner-text'>No duplicates found in {destinationLabel}. This product will be created fresh there.</p>
              </div>
            )}

            <button className='mpo-btn-primary' onClick={handleMove} disabled={(matches.length > 0 && !choice) || loading}>
              {loading ? <ButtonLoaderII /> : choice === "restock" ? "Restock existing product" : "Create new product & move"}
            </button>
            <button className='mpo-btn-secondary' onClick={onClose}>
              Cancel
            </button>
          </>
        )}

        {/* ── STEP 3: SUCCESS ── */}
        {step === STEPS.SUCCESS && (
          <div className='mpo-success-step'>
            <div className='mpo-success-icon-wrap'>
              <svg width='28' height='28' viewBox='0 0 24 24' fill='none' stroke='#16a34a' strokeWidth='2'>
                <path d='M22 11.08V12a10 10 0 11-5.93-9.14' />
                <polyline points='22 4 12 14.01 9 11.01' />
              </svg>
            </div>
            <h2 className='mpo-success-step-title'>Move successful</h2>
            <p className='mpo-success-step-msg'>
              <b>{quantity} units</b> of <b>{product.name}</b> moved to <b>{destinationLabel}</b>
              {choice === "restock" ? " and restocked to the existing product." : ", added as a new product entry."}
            </p>
            <button className='mpo-btn-primary' onClick={onClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
